import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  BlackbaudRaisersEdgeContactMappingStore,
  BlackbaudRaisersEdgeIntegration,
} from '@feathr/blackbox';
import { toast } from '@feathr/components';

/**
 * Returns the integration's shared contact mapping store and loads it on mount. A store that was
 * loaded before is reloaded, so a remount picks up server-side changes such as a finished
 * provisioning or a new sync date. A failed first load toasts; a failed reload keeps the list as
 * it was.
 *
 * Call it from one component per page, the owner of the load. Other components read
 * `integration.contactMappingStore` directly. Read it from an `observer` component so the
 * component re-renders when the mappings change.
 */
function useRaisersEdgeContactMappings(
  integration: BlackbaudRaisersEdgeIntegration,
): BlackbaudRaisersEdgeContactMappingStore {
  const { t } = useTranslation();
  const store = integration.contactMappingStore;

  useEffect(() => {
    let isMounted = true;

    async function load(): Promise<void> {
      await (store.isFetched ? store.refresh() : store.fetch());
      if (isMounted && !store.isFetched && store.error) {
        toast(t("Couldn't load mappings. {{error}}", { error: store.error }), { type: 'error' });
      }
    }

    void load();

    return (): void => {
      isMounted = false;
    };
  }, [store]);

  return store;
}

export default useRaisersEdgeContactMappings;
